import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
    headOfficeFinanceApi,
    LedgerType,
    LedgerResponse,
    LedgerSummaryItem,
    LedgerFilters,
} from '../../services/headOfficeFinanceApi';

interface HeadOfficeFinanceState {
    ledgerTypes: LedgerType[];
    currentLedger: LedgerResponse | null;
    summary: LedgerSummaryItem[];
    selectedType: string;
    filters: {
        from_date: string;
        to_date: string;
        per_page: number;
        page: number;
    };
    loading: boolean;
    summaryLoading: boolean;
    error: string | null;
}

const initialState: HeadOfficeFinanceState = {
    ledgerTypes: [],
    currentLedger: null,
    summary: [],
    selectedType: 'CIB',
    filters: {
        from_date: '',
        to_date: '',
        per_page: 50,
        page: 1,
    },
    loading: false,
    summaryLoading: false,
    error: null,
};

// Async thunks
export const fetchLedgerTypes = createAsyncThunk(
    'headOfficeFinance/fetchLedgerTypes',
    async (_, { rejectWithValue }) => {
        try {
            const response = await headOfficeFinanceApi.getLedgerTypes();
            return response;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch ledger types');
        }
    }
);

export const fetchLedger = createAsyncThunk(
    'headOfficeFinance/fetchLedger',
    async (filters: LedgerFilters, { rejectWithValue }) => {
        try {
            const response = await headOfficeFinanceApi.getLedger(filters);
            return response;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch ledger');
        }
    }
);

export const fetchSummary = createAsyncThunk(
    'headOfficeFinance/fetchSummary',
    async (_, { rejectWithValue }) => {
        try {
            const response = await headOfficeFinanceApi.getSummary();
            return response;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch summary');
        }
    }
);

const headOfficeFinanceSlice = createSlice({
    name: 'headOfficeFinance',
    initialState,
    reducers: {
        setSelectedType: (state, action: PayloadAction<string>) => {
            state.selectedType = action.payload;
            state.filters.page = 1;
        },
        setFilters: (state, action: PayloadAction<Partial<HeadOfficeFinanceState['filters']>>) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        setPage: (state, action: PayloadAction<number>) => {
            state.filters.page = action.payload;
        },
        resetFilters: (state) => {
            state.filters = {
                from_date: '',
                to_date: '',
                per_page: 50,
                page: 1,
            };
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch Ledger Types
        builder
            .addCase(fetchLedgerTypes.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchLedgerTypes.fulfilled, (state, action) => {
                state.loading = false;
                state.ledgerTypes = action.payload;
            })
            .addCase(fetchLedgerTypes.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Fetch Ledger
        builder
            .addCase(fetchLedger.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchLedger.fulfilled, (state, action) => {
                state.loading = false;
                state.currentLedger = action.payload;
            })
            .addCase(fetchLedger.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Fetch Summary
        builder
            .addCase(fetchSummary.pending, (state) => {
                state.summaryLoading = true;
                state.error = null;
            })
            .addCase(fetchSummary.fulfilled, (state, action) => {
                state.summaryLoading = false;
                state.summary = action.payload;
            })
            .addCase(fetchSummary.rejected, (state, action) => {
                state.summaryLoading = false;
                state.error = action.payload as string;
            });
    },
});

export const {
    setSelectedType,
    setFilters,
    setPage,
    resetFilters,
    clearError,
} = headOfficeFinanceSlice.actions;

export default headOfficeFinanceSlice.reducer;
